/* ── Barre d'onglets mobile ───────────────────────────────────────────
   Reprise du motif de la charte : barre noire arrondie collée en bas,
   icônes filaires blanches, bouton d'action rose en débord au centre.
   Visible en dessous de md uniquement — au-dessus, la sidebar noire
   assure la navigation. */

export type MobileTab = 'home' | 'history' | 'pricing' | 'account';

interface Props {
  active: MobileTab | null;
  onHome: () => void;
  onHistory: () => void;
  onPricing: () => void;
  onAccount: () => void;
  /** Action centrale — nouvelle analyse. */
  onNew: () => void;
  /** Pastille sur l'onglet Historique (analyses non lues). */
  badge?: number;
}

function TabButton({
  label, active, onClick, children,
}: { label: string; active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`relative flex h-11 w-11 items-center justify-center rounded-full transition-colors ${
        active ? 'text-rose-pastel-400' : 'text-white/55 hover:text-white'
      }`}
    >
      {children}
      {active && (
        <span className="absolute -bottom-0.5 h-1 w-1 rounded-full bg-rose-pastel-400" />
      )}
    </button>
  );
}

export default function MobileTabBar({
  active, onHome, onHistory, onPricing, onAccount, onNew, badge = 0,
}: Props) {
  return (
    <div className="md:hidden shrink-0 px-3 pb-3 pt-1">
      <nav className="panel-ink relative flex items-center justify-between rounded-full px-4 py-2 shadow-[0_10px_30px_-12px_rgba(16,16,16,0.55)]">
        <TabButton label="Accueil" active={active === 'home'} onClick={onHome}>
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </TabButton>

        <TabButton label="Historique" active={active === 'history'} onClick={onHistory}>
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="4" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
          {badge > 0 && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-rose-pastel-400 ring-2 ring-ink" />
          )}
        </TabButton>

        {/* Bouton d'action central, en débord — le « + » rose de la charte */}
        <button
          onClick={onNew}
          aria-label="Nouvelle analyse"
          className="-mt-7 flex h-14 w-14 items-center justify-center rounded-full bg-rose-pastel-400 text-ink shadow-[0_8px_20px_-6px_rgba(242,160,206,0.9)] ring-4 ring-cream-app transition-transform active:scale-95"
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>

        <TabButton label="Tarifs" active={active === 'pricing'} onClick={onPricing}>
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <circle cx="7" cy="7" r="1.2" />
          </svg>
        </TabButton>

        <TabButton label="Mon compte" active={active === 'account'} onClick={onAccount}>
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </TabButton>
      </nav>
    </div>
  );
}
